/**
 * Expandable card for a data source showing its fields
 */

import { useState } from 'react'
import { ChevronDown, ChevronRight, Database, RefreshCw, AlertCircle } from 'lucide-react'
import { useDataSourceDetail, useResetFieldLabels } from '../hooks/queries'
import type { DataSourceInfo } from '../types'
import { FieldLabelRow } from './FieldLabelRow'

interface DataSourceCardProps {
  source: DataSourceInfo
}

export function DataSourceCard({ source }: DataSourceCardProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [showResetConfirm, setShowResetConfirm] = useState(false)

  const { data: detail, isLoading, error } = useDataSourceDetail(source.table_name, isExpanded)
  const resetMutation = useResetFieldLabels(source.table_name)

  const handleReset = async () => {
    await resetMutation.mutateAsync()
    setShowResetConfirm(false)
  }

  const fields = detail?.fields || []
  const hasCustomLabels = fields.some((f) => f.custom_label !== null)

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400" />
          )}
          <Database className="w-5 h-5 text-blue-500" />
          <div className="text-left">
            <div className="font-medium text-gray-900">{source.display_name}</div>
            {source.description && (
              <div className="text-sm text-gray-500">{source.description}</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span className="bg-gray-100 px-2 py-0.5 rounded">{source.provider}</span>
          <span>{source.record_count.toLocaleString('de-DE')} Datensätze</span>
          {source.years_available.length > 0 && (
            <span>
              {source.years_available[0]}
              {source.years_available.length > 1 && ` - ${source.years_available[source.years_available.length - 1]}`}
            </span>
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-200">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900" />
            </div>
          ) : error ? (
            <div className="p-4 flex items-center gap-2 text-red-600">
              <AlertCircle className="w-4 h-4" />
              <span>Fehler beim Laden der Feldlabels</span>
            </div>
          ) : (
            <>
              {/* Actions Bar */}
              <div className="px-4 py-2 bg-gray-50 flex items-center justify-between border-b border-gray-200">
                <span className="text-sm text-gray-600">
                  {fields.length} Felder
                  {hasCustomLabels && (
                    <span className="ml-2 text-blue-600">(angepasst)</span>
                  )}
                </span>
                {hasCustomLabels && (
                  <>
                    {showResetConfirm ? (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Alle Labels zurücksetzen?</span>
                        <button
                          onClick={handleReset}
                          disabled={resetMutation.isPending}
                          className="px-2 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                        >
                          {resetMutation.isPending ? 'Wird zurückgesetzt...' : 'Ja'}
                        </button>
                        <button
                          onClick={() => setShowResetConfirm(false)}
                          className="px-2 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                        >
                          Nein
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowResetConfirm(true)}
                        className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Zurücksetzen
                      </button>
                    )}
                  </>
                )}
              </div>

              {/* Fields Table */}
              <div className="divide-y divide-gray-100">
                <div className="grid grid-cols-12 gap-4 px-4 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase">
                  <div className="col-span-3">Feldname</div>
                  <div className="col-span-3">Standard-Label</div>
                  <div className="col-span-4">Anzeige-Label</div>
                  <div className="col-span-1 text-center">Typ</div>
                  <div className="col-span-1 text-center">Sichtbar</div>
                </div>
                {fields
                  .sort((a, b) => a.display_order - b.display_order)
                  .map((field) => (
                    <FieldLabelRow
                      key={field.id}
                      field={field}
                      sourceTable={source.table_name}
                    />
                  ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
